import { useQuery, useMutation, useQueryClient } from 'react-query';
import { flowButtonService, type FlowButtonListFilter } from '@/services/flowButtonService';
import type { IFlowButton, IFlowButtonCreateRequest, IFlowButtonUpdateRequest } from '@/types/flowbutton';

export const FLOW_BUTTON_QUERY_KEYS = {
  all: ['flow-buttons'] as const,
  lists: () => [...FLOW_BUTTON_QUERY_KEYS.all, 'list'] as const,
  list: (filter?: FlowButtonListFilter) => [...FLOW_BUTTON_QUERY_KEYS.lists(), filter] as const,
  detail: (id: string) => [...FLOW_BUTTON_QUERY_KEYS.all, 'detail', id] as const,
};

export const useFlowButtons = (filter?: FlowButtonListFilter) => {
  return useQuery<IFlowButton[], Error>(
    FLOW_BUTTON_QUERY_KEYS.list(filter),
    () => flowButtonService.list(filter),
    {
      staleTime: 30_000,
      keepPreviousData: true,
    }
  );
};

export const useFlowButton = (id: string | undefined) => {
  return useQuery<IFlowButton, Error>(
    FLOW_BUTTON_QUERY_KEYS.detail(id!),
    () => flowButtonService.get(id!),
    { enabled: !!id }
  );
};

export const useCreateFlowButton = () => {
  const queryClient = useQueryClient();
  return useMutation<IFlowButton, Error, IFlowButtonCreateRequest>(
    (data) => flowButtonService.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FLOW_BUTTON_QUERY_KEYS.lists());
      },
    }
  );
};

export const useUpdateFlowButton = () => {
  const queryClient = useQueryClient();
  return useMutation<IFlowButton, Error, { id: string; data: IFlowButtonUpdateRequest }>(
    ({ id, data }) => flowButtonService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FLOW_BUTTON_QUERY_KEYS.lists());
      },
    }
  );
};

export const useDeleteFlowButton = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>(
    (id) => flowButtonService.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FLOW_BUTTON_QUERY_KEYS.lists());
      },
    }
  );
};
